import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import type { Action, Location } from 'history';
import { useHistory } from 'react-router-dom';
import {
  UnsavedChangesProvider,
  useUnsavedChangesContext
} from './_private/UnsavedChangesContext';
import {
  UnsavedChangesDialog,
  UnsavedChangesDialogProps
} from './_private/UnsavedChangesDialog';
import {
  usePreventUnsavedChangesDrop,
  UsePreventUnsavedChangesDropOptions
} from './_private/usePreventUnsavedChangesDrop';

type PendingLeave =
  | { kind: 'navigation'; location: Location; action: Action }
  | { kind: 'dialog-close' }
  | { kind: 'request'; answer(leave: boolean): void };

export interface UnsavedChangesProtectionProps
  extends Pick<UsePreventUnsavedChangesDropOptions, 'beforeUnloadMessage'> {
  /**
   * Whether the form has unsaved changes
   */
  isDirty?: boolean;
  /**
   * Whether the dialog is open
   */
  open: boolean;
  /**
   * The original dialog onOpenChange handler
   */
  onOpenChange: (open: boolean) => void;
  /**
   * Protected content. Pass a node for pages, or a render prop when the dialog
   * needs the enhanced `onOpenChange` (blocks dismiss while dirty) or
   * `requestLeave` (asks before the host replaces the content itself, e.g. a
   * drawer showing another record; `true` at once while nothing is dirty).
   */
  children:
    | ReactNode
    | ((props: {
        open: boolean;
        onOpenChange: (open: boolean) => void;
        requestLeave: () => Promise<boolean>;
      }) => ReactNode);
  /**
   * Optional custom props for the UnsavedChangesDialog
   */
  dialogProps?: Partial<UnsavedChangesDialogProps>;
  /**
   * When this returns true for a target location, in-app navigation proceeds
   * without the unsaved-changes dialog (e.g. sibling tabs with shared state).
   * Use `createAllowedNavigationMatcher` from `v5/platform/react-router`.
   */
  allowNavigation?: (location: Location) => boolean;
}

/**
 * @experimental
 * Convenient wrapper for unsaved-changes protection (dialogs and pages).
 * Automatically renders the UnsavedChangesDialog and handles all the protection logic.
 * Also provides the UnsavedChangesContext to all children for managing multiple dirty states.
 *
 * While `open` and dirty: blocks in-app navigation (`history.block`), tab close
 * (`beforeunload`), and dialog dismiss (enhanced `onOpenChange` via render-prop children);
 * `requestLeave` (render prop) asks the same way before the host replaces the content.
 *
 * For pages, prefer {@link PageUnsavedChangesProtection}.
 */
export const UnsavedChangesProtection: React.FC<
  UnsavedChangesProtectionProps
> = props => {
  return (
    <UnsavedChangesProvider>
      <UnsavedChangesProtectionInner {...props} />
    </UnsavedChangesProvider>
  );
};

const UnsavedChangesProtectionInner: React.FC<
  UnsavedChangesProtectionProps
> = ({
  isDirty,
  open,
  onOpenChange,
  children,
  dialogProps,
  allowNavigation,
  ...options
}) => {
  const history = useHistory();
  const { hasUnsavedChanges: hasUnsavedChangesInsideContext } =
    useUnsavedChangesContext();
  const finalDirty = isDirty || hasUnsavedChangesInsideContext;
  const {
    isDialogOpen,
    closeDialog,
    handleConfirm,
    handleCancel,
    triggerProtection
  } = usePreventUnsavedChangesDrop(finalDirty, {
    preventBeforeUnload: open,
    ...options
  });

  const pendingLeaveRef = useRef<PendingLeave | null>(null);
  const unblockNavigationRef = useRef<(() => void) | undefined>();
  const triggerProtectionRef = useRef(triggerProtection);
  triggerProtectionRef.current = triggerProtection;
  const allowNavigationRef = useRef(allowNavigation);
  allowNavigationRef.current = allowNavigation;
  const finalDirtyRef = useRef(finalDirty);
  finalDirtyRef.current = finalDirty;

  const [navigationAllowed, setNavigationAllowed] = useState(false);

  /** Drops the pending leave; a waiting `requestLeave` gets `leave`. */
  const settlePendingLeave = useCallback((leave: boolean) => {
    const pending = pendingLeaveRef.current;
    pendingLeaveRef.current = null;
    if (pending?.kind === 'request') pending.answer(leave);
  }, []);

  useEffect(() => {
    if (!finalDirty) {
      setNavigationAllowed(false);
      // Nothing left to lose.
      settlePendingLeave(true);
    }
  }, [finalDirty, settlePendingLeave]);

  useEffect(() => {
    if (!open) {
      closeDialog();
      settlePendingLeave(false);
    }
  }, [open, closeDialog, settlePendingLeave]);

  // Nobody keeps waiting for a protection that is gone.
  useEffect(() => () => settlePendingLeave(false), [settlePendingLeave]);

  const requestLeave = useCallback((): Promise<boolean> => {
    if (!finalDirtyRef.current) return Promise.resolve(true);
    return new Promise<boolean>(answer => {
      settlePendingLeave(false);
      pendingLeaveRef.current = { kind: 'request', answer };
      triggerProtectionRef.current();
    });
  }, [settlePendingLeave]);

  const shouldBlockNavigation = open && finalDirty && !navigationAllowed;

  // useLayoutEffect so history.block is removed synchronously when open becomes
  // false (e.g. flushSync after programmatic close, before post-submit navigate).
  useLayoutEffect(() => {
    if (!shouldBlockNavigation) {
      return;
    }

    const unblock = history.block((location, action) => {
      if (allowNavigationRef.current?.(location)) {
        return;
      }

      // A host still waiting on `requestLeave` is answered: not this time.
      settlePendingLeave(false);
      pendingLeaveRef.current = { kind: 'navigation', location, action };
      triggerProtectionRef.current();

      // POP (back/forward): the URL updates before the blocker runs; undo it so
      // the address bar stays in sync until the user confirms in UnsavedChangesDialog.
      if (action === 'POP') {
        history.go(1);
      }

      return false;
    });

    unblockNavigationRef.current = unblock;

    return () => {
      unblock();
      unblockNavigationRef.current = undefined;
    };
  }, [shouldBlockNavigation, history]);

  const enhancedOnOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && finalDirty) {
      settlePendingLeave(false);
      pendingLeaveRef.current = { kind: 'dialog-close' };
      triggerProtection();
    } else {
      onOpenChange(nextOpen);
    }
  };

  const handleConfirmLeave = () => {
    handleConfirm();
    const pending = pendingLeaveRef.current;
    pendingLeaveRef.current = null;

    if (pending?.kind === 'navigation') {
      unblockNavigationRef.current?.();
      unblockNavigationRef.current = undefined;
      setNavigationAllowed(true);
      onOpenChange(false);
      history.push({
        pathname: pending.location.pathname,
        search: pending.location.search,
        hash: pending.location.hash,
        state: pending.location.state
      });
      return;
    }

    if (pending?.kind === 'dialog-close') {
      onOpenChange(false);
    }

    if (pending?.kind === 'request') {
      pending.answer(true);
    }
  };

  const handleCancelLeave = () => {
    settlePendingLeave(false);
    handleCancel();
  };

  const protectedChildren =
    typeof children === 'function'
      ? children({ open, onOpenChange: enhancedOnOpenChange, requestLeave })
      : children;

  return (
    <>
      {/* Stable wrapper so Dialog is not remounted when open → false (exit animation).
          Inline `display: contents`: legacy pages sit outside `.tailwind-root`, where the
          `contents` class does nothing and the wrapper would cut their height chain. */}
      <div style={{ display: 'contents' }}>{protectedChildren}</div>

      <UnsavedChangesDialog
        open={isDialogOpen}
        onOpenChange={handleCancelLeave}
        onConfirm={handleConfirmLeave}
        {...dialogProps}
      />
    </>
  );
};
